import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { BluebookAnnotationsContext } from 'chameleon/packages/blue-book-player/state/BluebookAnnotationsContext';
import { HIGHLIGHT_COLORS_NAMES } from 'chameleon/packages/blue-book-player/Annotation/constants';
import log from 'chameleon/packages/blue-book-player/utility/log';
import ButtonWithIcon from 'chameleon/packages/blue-book-player/library/button-with-icon';
import BlueBookButton from 'chameleon/packages/blue-book-player/library/bluebook-button/BlueBookButton';
import { findMatchingElement } from 'chameleon/packages/blue-book-player/library/NotesColumn/NotesList/utils';

const colorConfigs = {
  [HIGHLIGHT_COLORS_NAMES.YELLOW]: {
    default: 'bg-bluebook-yellow-highlight',
    active: 'bg-bluebook-yellow-highlight-selected',
  },
  [HIGHLIGHT_COLORS_NAMES.RED]: {
    default: 'bg-bluebook-red-highlight',
    active: 'bg-bluebook-red-highlight-selected',
  },
  [HIGHLIGHT_COLORS_NAMES.BLUE]: {
    default: 'bg-bluebook-blue-highlight',
    active: 'bg-bluebook-blue-highlight-selected',
  },
};

const NotesItem = (props) => {
  const {
    id,
    title,
    annotation,
    color,
    isActive,
    onSetActive,
    learnosityItemReference,
  } = props;
  const [text, setText] = useState(annotation);
  const [isTextChanged, setIsTextChanged] = useState(false);
  const [isDeleteMessageVisible, setIsDeleteMessageVisible] = useState(false);

  const textRef = useRef(null);
  const containerRef = useRef(null);
  const noButtonRef = useRef(null);
  const {
    state: { annotations },
    annotationManager: { editAnnotation, removeAnnotation },
  } = useContext(BluebookAnnotationsContext);

  const backgroundClass = isActive
    ? colorConfigs[color]?.active ||
      colorConfigs[HIGHLIGHT_COLORS_NAMES.YELLOW].active
    : colorConfigs[color]?.default ||
      colorConfigs[HIGHLIGHT_COLORS_NAMES.YELLOW].default;

  const handleClick = useCallback(() => {
    onSetActive(id);
    if (textRef.current) textRef.current.focus();
  }, [onSetActive, id]);

  useEffect(() => {
    if (isActive && !isDeleteMessageVisible) textRef?.current?.focus();
  }, [isActive]);

  useEffect(() => {
    if (isDeleteMessageVisible) {
      noButtonRef?.current?.focus();
    }
  }, [isDeleteMessageVisible, noButtonRef]);

  const handleTextChange = useCallback((event) => {
    setText(event.target.value);
    setIsTextChanged(true);
    onSetActive(id);
  }, []);

  const handleBlur = useCallback(
    async (event) => {
      if (isTextChanged) {
        try {
          await editAnnotation({ id, annotation: text });
        } catch (error) {
          log.error(`Error updating note ${id}:`, error);
        }
        setIsTextChanged(false);
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.relatedTarget)
      ) {
        const annotationItem = annotations.find((item) => item.id === id);
        if (!annotationItem) return;

        const matchingElement = findMatchingElement(
          annotationItem,
          learnosityItemReference,
        );

        if (matchingElement) {
          matchingElement.classList.remove(
            'lrn-annotations-texthighlight-marking--active',
          );
        }

        onSetActive(null);
        setIsDeleteMessageVisible(false);
      }
    },
    [isTextChanged, text, editAnnotation, id, onSetActive],
  );

  const handleFocus = useCallback(
    (event) => {
      onSetActive(id);
      if (event.target.closest('.delete-note-button')) {
        return;
      }
    },
    [onSetActive, id],
  );

  const handleDeleteClick = useCallback(
    (event) => {
      event.stopPropagation();
      setIsDeleteMessageVisible(true);
    },
    [id],
  );

  const handleTextareaChange = useCallback(
    (e) => {
      const textarea = e.target;
      handleTextChange(e);
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    },
    [handleTextChange],
  );
  useEffect(() => {
    if (textRef.current) {
      const textarea = textRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [text]);

  return (
    <div
      className={cn('notes-item relative', {
        'notes-item-active': isActive,
      })}
      role="button"
      tabIndex={-1}
      ref={containerRef}
      data-test-id={`notes-item-${id}`}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
    >
      <Fragment>
        <div
          className={cn('notes-item-header', backgroundClass)}
          data-test-id={`notes-item-title-${id}`}
        >
          <span className="font-bold py-3 truncate">{title}</span>
          <ButtonWithIcon
            className="bg-white delete-note-button"
            iconClassName="!fill-bluebook-light-gray"
            icon="highlight_delete"
            onClick={handleDeleteClick}
            isIcon
          />
        </div>
        <div className="notes-item-content">
          <textarea
            ref={textRef}
            value={text}
            onChange={handleTextareaChange}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            rows={1}
            placeholder="Notes are saved automatically."
            className="notes-item-textarea"
            data-test-id={`notes-item-textarea-${id}`}
            aria-label="Edit note text"
          />
        </div>
        {isDeleteMessageVisible && (
          <div className="grid border rounded-lg w-full h-full py-6 px-4 bg-black bg-opacity-75 absolute top-0 left-0 notes-diaglog">
            <h2 className="flex m-0 text-2xl text-white">Delete This Note?</h2>
            <div className="flex justify-end gap-x-10 items-center self-end">
              <BlueBookButton
                ref={noButtonRef}
                isInverted
                isNaked
                accessibility={{
                  'data-test-id': 'button-secondary',
                  'aria-label': 'button-secondary',
                }}
                onClick={() => setIsDeleteMessageVisible(false)}
                className="!font-bold !py-[7px] !px-[23px] !h-auto !leading-[1.71em]"
              >
                No
              </BlueBookButton>
              <BlueBookButton
                isInverted
                isNaked
                accessibility={{
                  'data-test-id': 'button-primary',
                  'aria-label': 'btnPrimary',
                }}
                onClick={() => removeAnnotation(id)}
                className=" !font-bold !py-[7px] !px-[23px] !h-auto !leading-[1.71em]"
              >
                Yes
              </BlueBookButton>
            </div>
          </div>
        )}
      </Fragment>
    </div>
  );
};

NotesItem.propTypes = {
  id: PropTypes.string.isRequired,
  annotation: PropTypes.string,
  title: PropTypes.string.isRequired,
  color: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  onSetActive: PropTypes.func.isRequired,
};

NotesItem.defaultProps = {
  annotation: '',
  color: HIGHLIGHT_COLORS_NAMES.YELLOW,
};

export default NotesItem;
